import {
  formatINR,
  sqft,
  formatDims,
  fileUrl,
  roomSqftBreakdown,
  roomsSqftBreakdown,
  formatSqftBreakdown,
  splitSqftBreakdown,
} from "@/lib/quotationApi";

export default function SummaryRooms({ data, tierList }) {
  return (
    <>
      <div className="card-luxe mb-4">
        <div className="overline text-white/50 mb-4">Areas & measurements</div>
        {(data.rooms || []).map((room, ri) => {
          return (
            <div key={room.id || `room-${ri}`} className="mb-5 last:mb-0 pb-4 last:pb-0 border-b border-white/5 last:border-0" data-testid={`summary-room-${ri}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="font-display font-semibold text-[15px]">
                  {room.area_type}
                  {room.description && (
                    <span className="text-white/40 font-normal text-sm">
                      {" "}· {room.description}
                    </span>
                  )}
                </div>
              </div>

              {room.images && room.images.length > 0 && (
                <div className="flex gap-2 mb-3 overflow-x-auto pb-1">
                  {room.images.map((img, ii) => (
                    <img
                      key={img.id || `img-${ri}-${ii}`}
                      src={fileUrl(img.storage_path)}
                      alt=""
                      className="w-16 h-16 rounded-lg object-cover border border-white/10 flex-shrink-0"
                    />
                  ))}
                </div>
              )}

              <div className="space-y-1.5 mb-3">
                {(room.measurements || []).map((m, mi) => {
                  const area = sqft(m);
                  return (
                    <div
                      key={m.id || `m-${ri}-${mi}`}
                      className="flex justify-between text-xs text-white/70 pl-3 border-l border-white/10 py-1"
                    >
                      <div>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span>{formatDims(m)} · {m.finish_name || "—"}</span>
                          {m.is_modular && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded-full border border-[#C5A880]/50 text-[#C5A880] uppercase tracking-wider">
                              Modular
                            </span>
                          )}
                        </div>
                        {m.description && (
                          <div className="text-white/40">{m.description}</div>
                        )}
                      </div>
                      <div className="text-right whitespace-nowrap text-white/50">
                        {area.toFixed(1)} sqft
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Per-room sqft breakdown */}
              <div
                className="flex justify-between items-center text-[12px] pl-3 pr-1 mb-3"
                data-testid={`summary-room-sqft-${ri}`}
              >
                <span className="text-white/45 uppercase tracking-wider text-[10px]">
                  Total area
                </span>
                <span className="text-white/85 font-medium">
                  {formatSqftBreakdown(roomSqftBreakdown(room))}
                </span>
              </div>

              {/* Per-room tier costs (dynamic) */}
              <div
                className="grid gap-2 mt-2"
                style={{
                  gridTemplateColumns: `repeat(${Math.max(1, tierList.length)}, minmax(0, 1fr))`,
                }}
                data-testid={`summary-room-tiers-${ri}`}
              >
                {tierList.map((t) => {
                  const rCost =
                    (t.breakdown.rooms || []).find((r) => r.id === room.id)?.area_cost || 0;
                  return (
                    <div
                      key={t.id}
                      className="rounded-lg border border-white/5 bg-white/[0.02] p-2"
                      data-testid={`summary-room-${ri}-${t.id}`}
                    >
                      <div
                        className="text-[9px] uppercase tracking-wider truncate"
                        style={{ color: t.color }}
                      >
                        {t.name}
                      </div>
                      <div
                        className="font-display font-semibold text-[13px] mt-0.5"
                        style={{ color: t.color }}
                      >
                        {formatINR(rCost)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Grand total sqft across all rooms */}
      {(data.rooms || []).length > 0 && (() => {
        const gs = splitSqftBreakdown(roomsSqftBreakdown(data.rooms));
        return (
          <div
            className="card-luxe !py-3 mb-4 flex justify-between items-center gap-3"
            data-testid="summary-grand-sqft"
          >
            <span className="overline text-white/50">Grand total area</span>
            <span className="text-right">
              <span className="font-display font-semibold text-[15px] text-[#C5A880]">
                {gs.main}
              </span>
              {gs.detail && (
                <span className="font-display text-[12px] text-white/45 ml-1.5">
                  {gs.detail}
                </span>
              )}
            </span>
          </div>
        );
      })()}
    </>
  );
}
